import { deleteParagraphBuilder, makeParagraphBuilder } from '@ck';
import type { Color, ColorMode, IColor } from '@color';
import { GOLDEN_RATIO, Size, size } from '@geom';
import { canvasKit, fontFamilies, fontMgr } from '@resources';
import { determineBaseTextDirection } from '@strings';
import { assertCondition, fixedNumber, newUuid, sha256Text } from '@utils';
import type { FontStyle as CKFontStyle, ParagraphStyle } from 'canvaskit';
import { BasicTextStyle } from './BasicTextStyle';
import {
  type LayoutTextOptions,
  type MeasuredText,
  type ParagraphOpts,
  textDirectionToCKTextDirection,
  WIDTH_EPSILON
} from './ckTextUtils';
import { ScalingFontSizeChooser } from './FontSizeChooser';
import { Paragraph } from './Paragraph';
import type { ParagraphCache } from './ParagraphCache';

export function layoutText(
  text: string,
  color: IColor,
  colorMode: ColorMode,
  layoutOptions: Partial<LayoutTextOptions>,
  paragraphCache: ParagraphCache,
  basicStyle: BasicTextStyle = BasicTextStyle.PLAIN
): MeasuredText {
  assertCondition(text.length > 0, 'Text must not be empty');
  const realizedColor = color.realize(colorMode);
  const mergedOptions: Partial<LayoutTextOptions> = {
    ...layoutOptions,
    basicTextStyle: layoutOptions.basicTextStyle ?? basicStyle
  };
  const { fontSize, size, paragraph, opts } = layoutParagraph(
    text,
    realizedColor,
    mergedOptions
  );

  const paragraphIdJSON = {
    text,
    color: realizedColor.toJSON(),
    colorMode,
    fontSize: fixedNumber(fontSize, 1),
    size: size.toString(1),
    style: opts.basicTextStyle ?? BasicTextStyle.PLAIN
  };
  const paragraphIdJSONString = JSON.stringify(paragraphIdJSON);
  const paragraphId = sha256Text(paragraphIdJSONString);
  paragraphCache.set(paragraphId, text, color, colorMode, opts, paragraph);
  return {
    text,
    fontSize,
    size,
    opts,
    paragraphId
  };
}

// ParagraphStyle is not a C++ Embind class, so it doesn't need to be deleted.
export function makeParagraphStyle(
  fontSize: number,
  text: string,
  color: Color,
  opts?: Partial<ParagraphOpts>
): ParagraphStyle {
  const fontStyle: CKFontStyle | undefined = (() => {
    const s = opts?.basicTextStyle ?? BasicTextStyle.PLAIN;
    switch (s) {
      case BasicTextStyle.BOLD:
        return {
          weight: canvasKit.FontWeight.Bold,
          slant: canvasKit.FontSlant.Upright
        } as CKFontStyle;
      case BasicTextStyle.ITALIC:
        return {
          weight: canvasKit.FontWeight.Normal,
          slant: canvasKit.FontSlant.Italic
        } as CKFontStyle;
      case BasicTextStyle.BOLD_ITALIC:
        return {
          weight: canvasKit.FontWeight.Bold,
          slant: canvasKit.FontSlant.Italic
        } as CKFontStyle;
      case BasicTextStyle.PLAIN:
      default:
        return {
          weight: canvasKit.FontWeight.Normal,
          slant: canvasKit.FontSlant.Upright
        } as CKFontStyle;
    }
  })();
  // Map basic style to variable font weight axis (wght) where available
  const fontVariations = (() => {
    const s = opts?.basicTextStyle ?? BasicTextStyle.PLAIN;
    const weight =
      s === BasicTextStyle.BOLD || s === BasicTextStyle.BOLD_ITALIC ? 700 : 400;
    return [{ axis: 'wght', value: weight }];
  })();

  return new canvasKit.ParagraphStyle({
    textStyle: {
      color: color.ckColor,
      fontFamilies,
      fontSize,
      fontStyle,
      fontVariations
    },
    textAlign: canvasKit.TextAlign.Start,
    textDirection: textDirectionToCKTextDirection(determineBaseTextDirection(text)),
    ellipsis: opts?.maxLines ? '…' : undefined,
    maxLines: opts?.maxLines
  });
}

export function makeParagraph(
  fontSize: number,
  text: string,
  color: Color,
  opts?: Partial<ParagraphOpts>
): Paragraph {
  const style = makeParagraphStyle(fontSize, text, color, opts);
  const builder = makeParagraphBuilder(style, fontMgr);
  builder.addText(text);
  const ckParagraph = builder.build();
  deleteParagraphBuilder(builder);

  // Create the MeasuredText object
  const measuredText: MeasuredText = {
    text,
    fontSize,
    size: size(ckParagraph.getMaxWidth(), ckParagraph.getHeight()),
    opts: opts ?? {},
    paragraphId: newUuid()
  };

  return new Paragraph(ckParagraph, measuredText);
}

export function withParagraph(
  fontSize: number,
  text: string,
  color: Color,
  fn: (paragraph: Paragraph) => void
) {
  const paragraph = makeParagraph(fontSize, text, color);
  try {
    fn(paragraph);
  } finally {
    paragraph.delete();
  }
}

export const TypographyConstants = {
  DEFAULT_ASPECT_RATIO: GOLDEN_RATIO,
  DEFAULT_PARAGRAPH_MIN_WIDTH: 30,
  DEFAULT_PARAGRAPH_MAX_WIDTH: 800
};

export function layoutParagraph(
  text: string,
  color: Color,
  layoutOptions: Partial<LayoutTextOptions>
): {
  fontSize: number;
  size: Size;
  paragraph: Paragraph;
  opts: LayoutTextOptions;
} {
  const fontSizeChooser = layoutOptions.fontSizeChooser ?? ScalingFontSizeChooser.DEFAULT;
  const aspectRatio =
    (layoutOptions.aspectRatio ?? 1) * TypographyConstants.DEFAULT_ASPECT_RATIO;
  let minWidth =
    layoutOptions.minWidth ?? TypographyConstants.DEFAULT_PARAGRAPH_MIN_WIDTH;
  const maxWidth =
    layoutOptions.maxWidth ?? TypographyConstants.DEFAULT_PARAGRAPH_MAX_WIDTH;
  if (minWidth > maxWidth) {
    minWidth = maxWidth;
  }
  const fontSize = fontSizeChooser.getFontSize(text);

  let width = 0;
  let height = 0;
  const maxLines = layoutOptions.maxLines;
  const basicTextStyle = layoutOptions.basicTextStyle ?? BasicTextStyle.PLAIN;
  const paragraph = makeParagraph(fontSize, text, color, {
    maxLines,
    basicTextStyle
  });

  // Lay out the paragraph as the minimum number of lines (newlines will add lines)
  paragraph.layout(maxWidth);
  width = Math.round(paragraph.getLongestLine()) + WIDTH_EPSILON;
  height = paragraph.getHeight();
  const minLineCount = paragraph.getNumberOfLines();
  const maxWordWidth = paragraph.getMinIntrinsicWidth();

  let layoutAspectRatio = width / height;
  let delta = Math.abs(layoutAspectRatio - aspectRatio);
  if (delta >= 0.9) {
    // Binary search to find the optimal width
    // The starting minimum width is the maximum of the given minimum width and the width of the longest word
    let lowerWidth = Math.max(minWidth, maxWordWidth);
    let upperWidth = width;
    let lastLineCount = 0;
    let lineCount = minLineCount;
    while (lineCount !== lastLineCount) {
      width = (upperWidth + lowerWidth) / 2;
      paragraph.layout(width);
      lastLineCount = lineCount;
      lineCount = paragraph.getNumberOfLines();

      const layoutWidth = Math.round(paragraph.getLongestLine()) + WIDTH_EPSILON;

      height = paragraph.getHeight();
      layoutAspectRatio = layoutWidth / height;
      delta = Math.abs(layoutAspectRatio - aspectRatio);
      if (delta < 0.2) {
        break;
      }
      if (layoutAspectRatio < aspectRatio) {
        lowerWidth = layoutWidth;
      } else {
        upperWidth = layoutWidth;
      }
    }
    width = Math.round(paragraph.getLongestLine()) + WIDTH_EPSILON;
  }

  const opts = {
    fontSizeChooser,
    minWidth,
    maxWidth,
    aspectRatio,
    maxLines,
    basicTextStyle
  };
  return { fontSize, size: size(width, height), paragraph, opts };
}
